import DOMPurify from 'dompurify';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import {
    useEditor,
    Editor,
    Extensions
} from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Link from '@tiptap/extension-link';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import Text from '@tiptap/extension-text';
import {
    EditorEvents,
    Extension
} from '@tiptap/core';
import {
    MultiSelectNode,
    OptionNode,
    ProductNode,
    RatingNode,
    SelectNode
} from './ChatBotExtensions';
import { allowedLinkOrigins } from './allowedLinkOrigins';

export const isAllowedUrl = (url: string) => {
    try {
        const urlObj = new URL(url);
        // Validate URL scheme to prevent javascript: URLs
        if (!['http:', 'https:'].includes(urlObj.protocol)) {
            return false;
        }
        
        return allowedLinkOrigins.has(urlObj.origin);
    } catch (err) {
        return false;
    }
};

const sanitizeHtml = (html: string) => {
    return DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_TAGS: [
            'feedback',
            'product',
            'rating',
            'multiselect'
        ],
        ADD_ATTR: [
            'desc',
            'image-url',
            'markdown-price',
            'price',
            'slug',
            'submitLabel',
            'subtext',
            'subtitle',
            'showRating',
            'ratingCount',
            'title',
            'variant',
        ],
    });
};

const submit = (
    editor: Editor | null,
    submitter: (text: string) => Promise<void>,
) => {
    if (editor && !editor.isDestroyed) {
        const text = editor.getText().trim();
        if (text) {
            submitter(text);
        }
        return true;
    }
    return false;
};

const createKeymap = (
    submitEditorMessage: (editor: Editor | null) => boolean,
) => {
    return Extension.create({
        name: 'chat_keymap',
        addKeyboardShortcuts() {
            return {
                Enter: () => {
                    submitEditorMessage(this.editor as Editor);
                    return true;
                },
            }
        },
    });
};

export type TipTapEditorProps = {
    placeholder?: string;
    submitter: (text: string) => Promise<void>;
    onUpdate?: (props: EditorEvents['update']) => void;
    sendMessage: (text: string) => Promise<void>;
    editable?: boolean;
    ariaLabel?: string;
};

export function useTipTapEditor (props: TipTapEditorProps) {
    const {
        placeholder,
        submitter,
        onUpdate,
        ariaLabel = 'textbox',
        editable = true,
    } = props;

    const submitterRef = useRef(submitter);
    useEffect(() => {
        submitterRef.current = submitter;
    }, [submitter]);

    const submitEditorMessage = useCallback((editor: Editor | null) => {
        if (submitterRef.current) {
            return submit(editor, submitterRef.current);
        }
        return false;
    }, [submitterRef]);

    const extensions = useMemo(() => {
        const keymap = createKeymap(submitEditorMessage);

        const extensions: Extensions = [
            Document,
            Paragraph,
            Text,
            Placeholder.configure({ 
                placeholder,
                showOnlyWhenEditable: false,
            }),
            keymap,
        ];

        return extensions;
    }, [submitEditorMessage, placeholder]);

    const editor = useEditor({
        extensions,
        editorProps: {
            attributes: {
                'aria-label': ariaLabel,
                role: 'textbox',
                'aria-multiline': 'true',
            }
        },
        onUpdate: (props) => {
            if (onUpdate) {
                onUpdate(props);
            }
        },
        editable,
        autofocus: editable,
    }, [editable]);

    const focus = useCallback(() => {
        editor?.commands.focus();
    }, [editor]);

    const clear = useCallback(() => {
        editor?.commands.clearContent();
    }, [editor]);

    return {
        editor,
        submit: () => submitEditorMessage(editor),
        focus,
        clear,
        characterCount: editor?.getText().length ?? 0,
    };
}

export interface UseTipTapViewerProps {
    message: string;
    textOnly: boolean;
    ariaLabel?: string;
}

export function useTipTapViewer ({
    message,
    textOnly,
    ariaLabel = 'textbox',
}: UseTipTapViewerProps) {
    const purifiedHtml = useMemo(() => sanitizeHtml(message || ''), [message]);
    
    const extensions = useMemo(() => {
        if (textOnly) {
            // Customer: Only Paragraph and Text
            return [
                Document,
                Paragraph,
                Text,
            ];
        } else {
            // Agent/Bot: Full-featured set
            return [
                StarterKit,
                Link.configure({
                    isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && isAllowedUrl(url),
                }),
                MultiSelectNode,
                OptionNode,
                ProductNode,
                SelectNode,
                RatingNode,
            ];
        }
    }, [textOnly]);

    const editor = useEditor({
        extensions,
        editorProps: {
            attributes: {
                'aria-label': ariaLabel,
                role: 'textbox',
                'aria-multiline': 'true',
                'aria-readonly': 'true',
            }
        },
        content: purifiedHtml,
        editable: false,
    }, [extensions, purifiedHtml]);

    return editor;
}
